import { useState, useEffect } from "react";
import { CurrencyEur, TrendUp, MapPin, Package, ArrowDown, Clock, Receipt, ChartBar } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POINT_TYPE_LABELS = { bus_station: "Est. Autocarro", train_station: "Est. Comboio", courier: "Estafeta", shop: "Loja/Quiosque" };
const POINT_TYPE_COLORS = { bus_station: "#7c3aed", train_station: "#2563eb", courier: "#D96C4A", shop: "#3E6656" };

export default function FinancialDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("blumove_token");
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/intermediary/financial`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="pt-20 px-6 text-center"><p style={{ color: "var(--text-muted)" }}>A carregar dados financeiros...</p></div>;
  if (!data) return <div className="pt-20 px-6 text-center"><p style={{ color: "var(--text-muted)" }}>Sem dados disponiveis</p></div>;

  return (
    <div data-testid="financial-dashboard" className="pt-20 px-6 pb-12">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 animate-fade-in-up">
          <div className="flex items-center gap-2 mb-1">
            <CurrencyEur size={20} weight="fill" style={{ color: "var(--success)" }} />
            <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--success)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Financeiro</span>
          </div>
          <h1 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.6rem" }}>Comissoes e receitas</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Dashboard financeiro dos pontos da rede Bluemove</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 animate-fade-in-up stagger-1">
          <div className="bento-item p-5">
            <div className="flex items-center gap-2 mb-2">
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(39,174,96,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CurrencyEur size={18} weight="duotone" style={{ color: "#27ae60" }} />
              </div>
            </div>
            <span data-testid="total-earned" style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.6rem", color: "#27ae60", display: "block" }}>{data.total_earned.toFixed(2)}</span>
            <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase" }}>Total ganho (EUR)</span>
          </div>
          <div className="bento-item p-5">
            <div className="flex items-center gap-2 mb-2">
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(232,163,89,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Clock size={18} weight="duotone" style={{ color: "var(--warning)" }} />
              </div>
            </div>
            <span data-testid="total-pending" style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.6rem", color: "var(--warning)", display: "block" }}>{data.total_pending.toFixed(2)}</span>
            <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase" }}>Pendente (EUR)</span>
          </div>
          <div className="bento-item p-5">
            <div className="flex items-center gap-2 mb-2">
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(37,99,235,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <MapPin size={18} weight="duotone" style={{ color: "#2563eb" }} />
              </div>
            </div>
            <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.6rem", display: "block" }}>{data.total_points}</span>
            <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase" }}>Pontos ativos</span>
          </div>
          <div className="bento-item p-5">
            <div className="flex items-center gap-2 mb-2">
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(45,74,62,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TrendUp size={18} weight="duotone" style={{ color: "var(--primary)" }} />
              </div>
            </div>
            <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.6rem", display: "block" }}>{(data.total_earned + data.total_pending).toFixed(2)}</span>
            <span style={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase" }}>Receita total (EUR)</span>
          </div>
        </div>

        {/* Commission rates */}
        <div className="bento-item p-5 mb-6 animate-fade-in-up stagger-2">
          <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <Receipt size={16} weight="duotone" style={{ color: "var(--primary)" }} /> Tabela de comissoes
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div style={{ padding: 14, borderRadius: 12, background: "var(--bg)", textAlign: "center" }}>
              <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", color: "var(--primary)", display: "block" }}>{data.commission_rates.pickup.toFixed(2)} EUR</span>
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Por recolha processada</span>
            </div>
            <div style={{ padding: 14, borderRadius: 12, background: "var(--bg)", textAlign: "center" }}>
              <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", color: "var(--success)", display: "block" }}>{data.commission_rates.delivery.toFixed(2)} EUR</span>
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Por entrega processada</span>
            </div>
            <div style={{ padding: 14, borderRadius: 12, background: "var(--bg)", textAlign: "center" }}>
              <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.3rem", color: "#2563eb", display: "block" }}>{data.commission_rates.storage_daily.toFixed(2)} EUR</span>
              <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Por bolsa armazenada/dia</span>
            </div>
          </div>
        </div>

        {/* Points table */}
        <div className="bento-item overflow-hidden animate-fade-in-up stagger-3" data-testid="financial-points-table">
          <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-color)" }}>
            <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem" }}>Detalhes por ponto</h3>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                  {["Ponto", "Tipo", "Cidade", "Recolhas", "Entregas", "Bolsas", "Comissao", "Pago", "Pendente"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.points.map(p => {
                  const ptColor = POINT_TYPE_COLORS[p.point_type] || "var(--text-muted)";
                  return (
                    <tr key={p.point_id} style={{ borderBottom: "1px solid var(--border-color)" }} data-testid={`financial-point-${p.point_name}`}>
                      <td style={{ padding: "10px 14px", fontWeight: 600, fontSize: "0.85rem" }}>{p.point_name}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ padding: "2px 8px", borderRadius: 9999, fontSize: "0.65rem", fontWeight: 600, background: `${ptColor}15`, color: ptColor }}>
                          {POINT_TYPE_LABELS[p.point_type] || p.point_type}
                        </span>
                      </td>
                      <td style={{ padding: "10px 14px", fontSize: "0.85rem", color: "var(--text-muted)" }}>{p.city}</td>
                      <td style={{ padding: "10px 14px", fontFamily: "JetBrains Mono", fontSize: "0.85rem" }}>{p.pickups}</td>
                      <td style={{ padding: "10px 14px", fontFamily: "JetBrains Mono", fontSize: "0.85rem" }}>{p.deliveries}</td>
                      <td style={{ padding: "10px 14px", fontFamily: "JetBrains Mono", fontSize: "0.85rem" }}>{p.bags_stored}</td>
                      <td style={{ padding: "10px 14px", fontFamily: "JetBrains Mono", fontWeight: 700, fontSize: "0.85rem", color: "var(--primary)" }}>{p.total_earned.toFixed(2)}</td>
                      <td style={{ padding: "10px 14px", fontFamily: "JetBrains Mono", fontSize: "0.85rem", color: "#27ae60" }}>{p.paid.toFixed(2)}</td>
                      <td style={{ padding: "10px 14px", fontFamily: "JetBrains Mono", fontSize: "0.85rem", color: "var(--warning)" }}>{p.pending.toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
